import { BottomSheet, Text, Title, type UseModalState } from "@clipboard-health/ui-components";
import { DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useCallback } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { BottomSheetHeader } from "../../components/BottomSheetHeader";
import { DialogFooter } from "../../components/DialogFooter";
import { LoadingButton } from "../../components/LoadingButton";
import {
  SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_MODAL_PATH,
} from "../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { type BlockingRequirementsContext } from "../Bookability/Documents/types";
import { convertModalShiftDataToUseBookShiftParams } from "../Booking/convertModalShiftDataToUseBookShiftParams";
import { useBookShift } from "../Booking/useBookShift";
import { ShiftBottomSheetBlockingRequirementsCard } from "../BottomSheet/BlockingRequirements/Card";
import { type ModalShiftData } from "../types";

interface DocumentResolutionBottomSheetProps {
  modalState: UseModalState;
  shift: ModalShiftData;
  blockingRequirementsContext: BlockingRequirementsContext;
}

export function DocumentResolutionBottomSheet(props: DocumentResolutionBottomSheetProps) {
  const { modalState, shift, blockingRequirementsContext } = props;

  const history = useHistory();
  const { navigateToModal, computedArrivalData, baseNavigationPath, baseNavigationPathOverride } =
    useShiftModalsDataContext();

  const { visibleBlockingRequirements, hasBlockingRequirements } = blockingRequirementsContext;

  const bookShiftParams = convertModalShiftDataToUseBookShiftParams({
    shift,
    navigateToModal,
    onBook: () => {
      navigateToModal(SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH, {
        pathParams: { shiftId: shift.id },
      });
    },
    computedArrivalData,
    rateNegotiationId: undefined,
  });

  const { attemptBookingShift, isBooking, isLoading } = useBookShift(bookShiftParams);

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_DETAILS_DOC_RESOLUTION_VIEWED,
    { shiftId: shift.id },
    { enabled: modalState.modalIsOpen }
  );

  const handleNavigateToDocumentDetails = useCallback(
    (documentDetailsPath: string) => {
      // Replace the current doc-resolution entry in the history stack with shift details,
      // so that browser back from document details returns to shift details (not doc-resolution).
      const effectiveBasePath = baseNavigationPathOverride
        ? `${baseNavigationPath}/${baseNavigationPathOverride}`
        : baseNavigationPath;
      const shiftDetailsPath = generatePath(
        `${effectiveBasePath}/${SHIFT_DISCOVERY_SHIFT_MODAL_PATH}`,
        { shiftId: shift.id }
      );
      history.replace(shiftDetailsPath);
      history.push(documentDetailsPath);
    },
    [history, shift.id, baseNavigationPath, baseNavigationPathOverride]
  );

  const handleBookShift = async () => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_CTA_BOOK_SHIFT, {
      shiftId: shift.id,
      source: "doc-resolution",
    });
    await attemptBookingShift();
  };

  return (
    <BottomSheet
      modalState={modalState}
      header={
        <BottomSheetHeader
          onBack={() => {
            modalState.closeModal();
          }}
        />
      }
      footer={
        <DialogFooter orientation="vertical" closeButtonAriaLabel="Close document resolution">
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            disabled={hasBlockingRequirements}
            isLoading={isLoading || isBooking}
            onClick={handleBookShift}
          >
            {hasBlockingRequirements ? "Complete documents to book" : "Book shift"}
          </LoadingButton>
        </DialogFooter>
      }
    >
      <DialogContent sx={{ backgroundColor: "unset", width: "100%", padding: 5 }}>
        <Stack direction="column" spacing={5}>
          <Stack spacing={3}>
            <Title variant="h4" component="h4" sx={{ textAlign: "left", textWrap: "balance" }}>
              Complete required documents
            </Title>
            <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              {visibleBlockingRequirements.length === 1
                ? "This document is required before you can book this shift. Complete it below to continue."
                : "These documents are required before you can book this shift. Complete them below to continue."}
            </Text>
          </Stack>

          <Stack spacing={4}>
            {visibleBlockingRequirements.map((requirement) => (
              <ShiftBottomSheetBlockingRequirementsCard
                key={requirement.requirementId}
                blockingRequirement={requirement}
                shiftId={shift.id}
                workplaceId={shift.workplace.id}
                onNavigateToDocDetails={handleNavigateToDocumentDetails}
              />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
